import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "../../../../app/hooks";
import { getParamAsInt } from "../../../../app/utils";
import { isAdminRole } from "../../../../app/userSlice";
import { useScrollToSection } from "../../../../app/useScrollToSection";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import { Loading } from "../../../../components/Loading";

import { DownloadButton } from "../../shared/DownloadButton";
import { LessonCategoryUsageChart } from "./LessonCategoryUsageChart";
import { AssignedLessonUsageChart } from "./AssignedLessonUsageChart";
import { WritingUsageChartWrapper } from "./WritingUsageChart";
import { UsageFilteredByCourseViewModel } from "./UsageFilteredByCourseViewModel";
import { useCourseUsageReportQuery } from "./courseUsageReportApiSlice";
import { UsageSummaryTable } from "./UsageSummaryTable";
import { WritingUsageTable } from "./WritingUsageTable";
import { PeerReviewUsageTable } from "./PeerReviewUsageTable";
import { LessonCategoryUsageTable } from "./LessonCategoryUsageTable";
import { AssignedLessonUsageTable } from "./AssignedLessonUsageTable";

export const USAGE_SUMMARY = "usage-summary";
export const USAGE_WRITING = "usage-writing";
export const USAGE_WRITING_CR = "usage-writing-cr";
export const USAGE_PEER_REVIEWS = "usage-peer-reviews";
export const USAGE_LESSONS = "usage-lessons";
export const USAGE_LESSONS_ASSIGNED = "usage-lessons-assigned";

export default function CourseUsageReport() {
	const [queryParams, setSearchParams] = useSearchParams();
	const courseIdParam = getParamAsInt(queryParams, "courseId") ?? null;

	const { aspUrl } = useAppSelector((state) => state.site);
	const { role } = useAppSelector((state) => state.user);
	useScrollToSection();

	const [view, setView] = useState("legacy");
	const [assignedLessonTimeMinutes, setAssignedLessonTimeMinutes] = useState(0);
	const [sortedData, setSortedData] = useState<UsageFilteredByCourseViewModel>();

	const result = useCourseUsageReportQuery(courseIdParam ? { courseId: courseIdParam } : {});

	useEffect(() => {
		if (!result.data) return;
		if (result.isError) return;

		setAssignedLessonTimeMinutes(
			result.data.assignedTutorialUsageForCourse.reduce(
				(timeSpent: number, { timeSpentMinutes }) => timeSpent + timeSpentMinutes,
				0
			)
		);

		const writingUsageByWriter = result.data.writingUsageByWriter.toSorted((a, b) =>
			a.writerName > b.writerName ? 1 : b.writerName > a.writerName ? -1 : 0
		);
		const constructedResponseUsageByWriter =
			result.data.constructedResponseUsageByWriter.toSorted((a, b) =>
				a.writerName > b.writerName ? 1 : b.writerName > a.writerName ? -1 : 0
			);
		const assignedTutorialUsageByWriter = result.data.assignedTutorialUsageByWriter.toSorted(
			(a, b) => (a.writerName > b.writerName ? 1 : b.writerName > a.writerName ? -1 : 0)
		);

		const _data = {
			...result.data,
			writingUsageByWriter: writingUsageByWriter,
			constructedResponseUsageByWriter: constructedResponseUsageByWriter,
			assignedTutorialUsageByWriter: assignedTutorialUsageByWriter,
		};

		setSortedData(_data);
	}, [result.data]);

	return (
		<main>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Class Usage Report</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "legacy", label: "Legacy" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "legacy" && (
				<div className="mt-8">
					<p>
						The class usage report displays the total number of essays and drafts, and
						the average number of drafts submitted per essay. The report also includes a
						peer review report to help teachers access and monitor student comments and
						usage. Teachers can also view student lesson usage within this report.
					</p>
					<section id={USAGE_SUMMARY} className="mt-4">
						<h2>Summary</h2>
						<Loading loading={result.isLoading} />
						{sortedData && (
							<UsageSummaryTable
								className="mt-4 mx-auto"
								data={sortedData}
								assignedLessonTimeMinutes={assignedLessonTimeMinutes}
							/>
						)}
					</section>

					<section id={USAGE_WRITING} className="mt-8">
						<h2>Writing</h2>
						<h3 className="mt-4">Essays</h3>
						<p className="mt-4">
							This section shows the number of essays, number of drafts submitted, and
							the average drafts per essay for essay writing. Click on a student name
							to view a summary of individual student usage data.
						</p>
						<Loading loading={result.isLoading} />
						{sortedData && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadWriterUsageFilteredByCourse?courseId=${sortedData.courseId}`}
										disabled={sortedData.writingUsageByWriter.length == 0}
									/>
								</div>
								<WritingUsageTable
									className="mt-4"
									courseId={sortedData.courseId}
									writingUsageByWriter={sortedData.writingUsageByWriter}
									writingUsageTotalForCourse={
										sortedData.writingUsageTotalForCourse
									}
								/>
								<WritingUsageChartWrapper
									className="mt-8"
									courseId={sortedData.courseId}
									title="Essay Writing Usage"
									writerData={sortedData.writingUsageByWriter}
									classData={sortedData.writingUsageAverageForCourse}
								/>
							</>
						)}
					</section>

					<section id={USAGE_WRITING_CR} className="mt-8">
						<h3>Constructed Response</h3>
						<p className="mt-4">
							This section shows the number of essays, number of drafts submitted, and
							the average drafts per essay for constructed response writing. Click on
							a student name to view a summary of individual student usage data.
						</p>
						<Loading loading={result.isLoading} />
						{sortedData && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadConstructedResponseWriterUsageFilteredByCourse?courseId=${sortedData.courseId}`}
										disabled={
											sortedData.constructedResponseUsageByWriter.length == 0
										}
									/>
								</div>
								<WritingUsageTable
									className="mt-4"
									courseId={sortedData.courseId}
									writingUsageByWriter={
										sortedData.constructedResponseUsageByWriter
									}
									writingUsageTotalForCourse={
										sortedData.constructedResponseUsageTotalForCourse
									}
								/>
								<WritingUsageChartWrapper
									className="mt-6"
									title="Constructed Response Usage"
									courseId={sortedData.courseId}
									writerData={sortedData.constructedResponseUsageByWriter}
									classData={sortedData.constructedResponseUsageAverageForCourse}
								/>
							</>
						)}
					</section>

					<section id={USAGE_PEER_REVIEWS} className="mt-8">
						<h2>Peer Reviews</h2>
						<p className="mt-4">
							The section below shows completed peer reviews within a class including
							the date of the review, the name of the prompt, the reviewer name and
							the author of the writing.{" "}
							{!isAdminRole(role) ? "To read the review, click on the date." : ""}
						</p>
						<Loading loading={result.isLoading} />
						{sortedData && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadPeerReviewUsageFilteredByCourse?courseId=${sortedData.courseId}`}
										disabled={sortedData.peerReviews.length == 0}
									/>
								</div>
								<PeerReviewUsageTable
									className="mt-4"
									peerReviews={sortedData.peerReviews}
								/>
							</>
						)}
					</section>

					<section id={USAGE_LESSONS} className="mt-8">
						<h2>Lessons</h2>
						<h3 className="mt-4">Lesson Usage</h3>
						<p className="mt-4">
							This section shows the most recent date of use and the total time spent
							using the lessons within each category for the selected class. Click on
							the category title to view the dates of use and time spent on specific
							lessons within each area.
						</p>
						<Loading loading={result.isLoading} />
						{sortedData && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadTutorialUsageFilteredByCourse?courseId=${sortedData.courseId}`}
										disabled={sortedData.tutorialUsageByCategory.length == 0}
									/>
								</div>
								<LessonCategoryUsageTable
									className="mt-4"
									courseId={sortedData.courseId}
									tutorialUsageByCategory={sortedData.tutorialUsageByCategory}
									tutorialUsageForCourse={sortedData.tutorialUsageForCourse}
								/>
								<LessonCategoryUsageChart
									className="mt-6"
									courseId={sortedData.courseId}
									data={sortedData.tutorialUsageByCategory}
								/>
							</>
						)}
					</section>

					<section id={USAGE_LESSONS_ASSIGNED} className="mt-8">
						<h3>Assigned Lesson Usage</h3>
						<p className="mt-4">
							This section shows the name of the assigned lesson, the number of
							students assigned to the lesson, and the time spent on each. The chart
							shows the total number of assigned and completed lessons for each
							student.
						</p>
						<Loading loading={result.isLoading} />
						{sortedData && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadAssignedTutorialUsageFilteredByCourse?courseId=${sortedData.courseId}`}
										disabled={
											sortedData.assignedTutorialUsageForCourse.length == 0
										}
									/>
								</div>
								<AssignedLessonUsageTable
									className="mt-4"
									assignedTutorialUsageForCourse={
										sortedData.assignedTutorialUsageForCourse
									}
									assignedLessonTimeMinutes={assignedLessonTimeMinutes}
								/>
								<AssignedLessonUsageChart
									className="mt-6"
									courseId={sortedData.courseId}
									data={sortedData.assignedTutorialUsageByWriter}
								/>
							</>
						)}
					</section>
				</div>
			)}
			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Usage Report Data" result={result} />
				</>
			)}
		</main>
	);
}

export function studentUsageLink(courseId?: number, writerId?: number) {
	if (!courseId && !writerId) return `/reports/student/usage`;
	if (!courseId) return `/reports/student/usage?writerId=${writerId}`;
	if (!writerId) return `/reports/student/usage?courseId=${courseId}`;
	return `/reports/student/usage?writerId=${writerId}&courseId=${courseId}`;
}
